import { DomainValidationError } from "./identifiers.js";
const AUDIT_ACTION_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
const TARGET_TYPE_PATTERN = /^[a-z][a-z0-9_]*$/;
const MAX_REASON_LENGTH = 1000;
const MAX_ID_LENGTH = 128;
// Exhaustive over ActorKind at compile time; the database CHECK holds the same three values.
const ACTOR_KIND_RECORD = {
    customer: true,
    staff: true,
    system: true,
};
const ACTOR_KINDS = new Set(Object.keys(ACTOR_KIND_RECORD));
/** Bound on `before`/`after`, measured as the database measures it: `octet_length(jsonb::text)`. */
export const AUDIT_PAYLOAD_MAX_BYTES = 8192;
export function auditAction(value) {
    if (typeof value !== "string" || !AUDIT_ACTION_PATTERN.test(value)) {
        throw new DomainValidationError("auditAction", "must be dotted lowercase snake_case segments");
    }
    return value;
}
const METADATA_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
// Runtime proof that a definition came out of `defineAuditAction`, and one definition per action,
// so a forged or weaker redefinition cannot loosen the key allow-list or `requiresReason`.
const definitions = new WeakSet();
const definitionsByAction = new Map();
export function defineAuditAction(options) {
    const keys = options.metadataKeys;
    if (!Array.isArray(keys) || keys.some((key) => !METADATA_KEY_PATTERN.test(key))) {
        throw new DomainValidationError("metadataKeys", "must be an array of identifier-like keys of at most 64 characters");
    }
    if (new Set(keys).size !== keys.length) {
        throw new DomainValidationError("metadataKeys", "must not contain duplicates");
    }
    const action = auditAction(options.action);
    const requiresReason = options.requiresReason === true;
    const existing = definitionsByAction.get(action);
    if (existing !== undefined) {
        const sameKeys = existing.metadataKeys.length === keys.length &&
            keys.every((key) => existing.metadataKeys.includes(key));
        if (existing.requiresReason !== requiresReason || !sameKeys) {
            throw new DomainValidationError("auditAction", `${action} is already defined with different settings`);
        }
        return existing;
    }
    const definition = Object.freeze({
        action,
        requiresReason,
        metadataKeys: Object.freeze([...keys]),
    });
    definitions.add(definition);
    definitionsByAction.set(action, definition);
    return definition;
}
function utf8Length(text) {
    let bytes = 0;
    for (const char of text) {
        const code = char.codePointAt(0) ?? 0;
        bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
    }
    return bytes;
}
/**
 * Size of the payload as PostgreSQL renders it as jsonb text, which writes `": "` and `", "`
 * where JSON.stringify writes `":"` and `","`. That is 2n-1 extra bytes for n entries.
 */
export function jsonbTextSize(payload) {
    const entries = Object.keys(payload).length;
    const extra = entries === 0 ? 0 : 2 * entries - 1;
    return utf8Length(JSON.stringify(payload)) + extra;
}
const LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/;
// PostgreSQL rejects \u0000 in jsonb (22P05) and cannot encode a lone surrogate.
function isJsonbSafeString(value) {
    return !value.includes("\u0000") && !LONE_SURROGATE.test(value);
}
/** Validates a flat scalar object bounded at `maxBytes` of jsonb text. Null and undefined pass. */
export function assertFlatJsonObject(field, payload, maxBytes) {
    if (payload === null || payload === undefined)
        return;
    if (typeof payload !== "object" || Array.isArray(payload)) {
        throw new DomainValidationError(field, "must be a flat object");
    }
    for (const [key, value] of Object.entries(payload)) {
        if (!isJsonbSafeString(key)) {
            throw new DomainValidationError(field, "must not contain NUL or lone-surrogate characters");
        }
        if (typeof value === "string") {
            if (!isJsonbSafeString(value)) {
                throw new DomainValidationError(field, "must not contain NUL or lone-surrogate characters");
            }
            continue;
        }
        if (typeof value === "number") {
            if (!Number.isSafeInteger(value)) {
                throw new DomainValidationError(field, "numbers must be safe integers; use a string");
            }
            continue;
        }
        if (value !== null && typeof value !== "boolean") {
            throw new DomainValidationError(field, "must contain only string, number, boolean or null");
        }
    }
    if (jsonbTextSize(payload) > maxBytes) {
        throw new DomainValidationError(field, `must be at most ${maxBytes} bytes`);
    }
}
function assertLength(field, value, max) {
    if (typeof value !== "string" || value.length < 1 || value.length > max) {
        throw new DomainValidationError(field, `must be 1 to ${max} characters`);
    }
}
// Runtime proof that a draft came out of this module. A structural type cannot give that.
const accepted = new WeakMap();
function assertDefinition(definition) {
    if (typeof definition !== "object" || definition === null || !definitions.has(definition)) {
        throw new DomainValidationError("auditAction", "must be defined with defineAuditAction");
    }
}
function validateDraft(definition, draft) {
    assertDefinition(definition);
    if (typeof draft.actor?.kind !== "string" || !ACTOR_KINDS.has(draft.actor.kind)) {
        throw new DomainValidationError("actor.kind", "must be customer, staff or system");
    }
    assertLength("actor.id", draft.actor?.id, MAX_ID_LENGTH);
    assertLength("targetId", draft.targetId, MAX_ID_LENGTH);
    if (typeof draft.targetType !== "string" || !TARGET_TYPE_PATTERN.test(draft.targetType)) {
        throw new DomainValidationError("targetType", "must be lowercase snake_case");
    }
    const { reason } = draft;
    if (reason !== null && (typeof reason !== "string" || reason.length > MAX_REASON_LENGTH)) {
        throw new DomainValidationError("reason", `must be at most ${MAX_REASON_LENGTH} characters`);
    }
    if (definition.requiresReason && (reason === null || reason.trim().length === 0)) {
        throw new DomainValidationError("reason", "is required for this action");
    }
    for (const [field, payload] of [
        ["before", draft.before],
        ["after", draft.after],
    ]) {
        assertFlatJsonObject(field, payload, AUDIT_PAYLOAD_MAX_BYTES);
        if (payload !== null &&
            Object.keys(payload).some((k) => !definition.metadataKeys.includes(k))) {
            throw new DomainValidationError(field, "contains a key this action does not allow");
        }
    }
}
function register(definition, fields) {
    const draft = Object.freeze({
        ...fields,
        actor: Object.freeze({ kind: fields.actor.kind, id: fields.actor.id }),
        before: fields.before === null ? null : Object.freeze({ ...fields.before }),
        after: fields.after === null ? null : Object.freeze({ ...fields.after }),
    });
    accepted.set(draft, definition);
    return draft;
}
export function auditEventDraft(definition, input) {
    assertDefinition(definition);
    const fields = {
        actor: input.actor,
        action: definition.action,
        targetType: input.targetType,
        targetId: input.targetId,
        reason: input.reason ?? null,
        before: input.before ?? null,
        after: input.after ?? null,
        correlationId: input.correlationId ?? null,
        idempotencyKey: input.idempotencyKey ?? null,
    };
    validateDraft(definition, fields);
    return register(definition, fields);
}
export function isAuditEventDraft(value) {
    return typeof value === "object" && value !== null && accepted.has(value);
}
/**
 * Rejects anything not produced by `auditEventDraft` or `withAuditContext`, and re-runs every
 * rule on it, so a draft built with casts never reaches SQL.
 */
export function assertAuditEventDraft(draft) {
    const definition = typeof draft === "object" && draft !== null ? accepted.get(draft) : undefined;
    if (definition === undefined) {
        throw new DomainValidationError("auditEvent", "must be built with auditEventDraft");
    }
    validateDraft(definition, draft);
}
/** Attaches the unit of work's correlation id and idempotency key to an accepted draft. */
export function withAuditContext(draft, context) {
    assertAuditEventDraft(draft);
    const definition = accepted.get(draft);
    return register(definition, {
        actor: draft.actor,
        action: draft.action,
        targetType: draft.targetType,
        targetId: draft.targetId,
        reason: draft.reason,
        before: draft.before,
        after: draft.after,
        correlationId: context.correlationId,
        idempotencyKey: context.idempotencyKey,
    });
}
